/**
 * Helper functions to check user permissions and scopes based on userInfo data
 */

import type { UserInfo } from '@/core/collections/user-info/user-info.interface';

export interface UserScope {
   permissions?: string[];
   actions?: Record<string, string[]>;
   list?: string[];
}

export interface UserScopes {
   candidates?: UserScope;
   positions?: UserScope;
   customers?: UserScope;
   matching?: UserScope;
   follow_up?: {
      matches?: UserScope;
      journeys?: UserScope;
      tasks?: UserScope;
   };
   call_lists?: UserScope;
   communications?: UserScope;
   report?: UserScope;
}

/**
 * Check if user has a specific permission for a page
 * @param userInfo - The user information object
 * @param page - The page name (e.g., 'candidates', 'positions', 'follow_up.matches')
 * @param permission - The permission to check (e.g., 'view', 'create', 'update')
 * @returns boolean - true if user has the permission
 */
export function hasPermission(userInfo: UserInfo | null | undefined, page: string, permission: string): boolean {
   if (!userInfo?.scopes) {
      return false;
   }

   const scopes = userInfo.scopes as any;
   let pageScope: UserScope | undefined;

   // Handle nested pages with dot notation (e.g., 'follow_up.matches')
   if (page.includes('.')) {
      const [parentPage, subPage] = page.split('.');
      if (parentPage in scopes) {
         const parentScope = scopes[parentPage];
         pageScope = parentScope?.[subPage];
      }
   } else {
      pageScope = scopes[page];
   }

   return pageScope?.permissions?.includes(permission) ?? false;
}

/**
 * Check if user has access to a specific actionbar feature
 * @param userInfo - The user information object
 * @param page - The page name (e.g., 'candidates', 'positions', 'follow_up.matches')
 * @param actionType - The action type (e.g., 'mail', 'whatsapp', 'sms')
 * @param actionValue - The specific action value (e.g., 'general', 'position')
 * @returns boolean - true if user has access to the actionbar feature
 */
export function hasActionbarAccess(userInfo: UserInfo | null | undefined, page: string, actionType: string, actionValue?: string): boolean {
   if (!userInfo?.scopes) {
      return false;
   }

   const scopes = userInfo.scopes as any;
   let pageScope: UserScope | undefined;

   // Handle nested pages with dot notation (e.g., 'follow_up.matches')
   if (page.includes('.')) {
      const [parentPage, subPage] = page.split('.');
      if (parentPage in scopes) {
         const parentScope = scopes[parentPage];
         pageScope = parentScope?.[subPage];
      }
   } else {
      pageScope = scopes[page];
   }

   const actionbarConfig = pageScope?.actions?.[actionType];

   if (!actionbarConfig) {
      return false;
   }

   // If no specific action value is provided, check if the action type exists
   if (!actionValue) {
      return Array.isArray(actionbarConfig);
   }

   // Check if the specific action value is allowed
   return actionbarConfig.includes(actionValue);
}

/**
 * Check if user can view a specific list column
 * @param userInfo - The user information object
 * @param page - The page name (e.g., 'candidates', 'positions', 'follow_up.matches')
 * @param column - The column name to check
 * @returns boolean - true if user can view the column
 */
export function hasListColumnAccess(userInfo: UserInfo | null | undefined, page: string, column: string): boolean {
   if (!userInfo?.scopes) {
      return false;
   }

   const scopes = userInfo.scopes as any;
   let pageScope: UserScope | undefined;

   // Handle nested pages with dot notation (e.g., 'follow_up.matches')
   if (page?.includes('.')) {
      const [parentPage, subPage] = page.split('.');
      if (parentPage in scopes) {
         const parentScope = scopes[parentPage];
         pageScope = parentScope?.[subPage];
      }
   } else {
      pageScope = scopes[page];
   }

   return pageScope?.list?.includes(column) ?? false;
}

/**
 * Get all permissions for a specific page
 * @param userInfo - The user information object
 * @param page - The page name (e.g., 'candidates', 'positions', 'follow_up.matches')
 * @returns string[] - array of permissions
 */
export function getPagePermissions(userInfo: UserInfo | null | undefined, page: string): string[] {
   if (!userInfo?.scopes) {
      return [];
   }

   const scopes = userInfo.scopes as any;
   let pageScope: UserScope | undefined;

   // Handle nested pages with dot notation (e.g., 'follow_up.matches')
   if (page?.includes('.')) {
      const [parentPage, subPage] = page.split('.');
      if (parentPage in scopes) {
         const parentScope = scopes[parentPage];
         pageScope = parentScope?.[subPage];
      }
   } else {
      pageScope = scopes[page];
   }

   return pageScope?.permissions ?? [];
}
